/**
 * Factory owning the guard skeleton shared by the uid-scoped localStorage
 * stores (chatReadPositionStorage / drawerLayoutStorage /
 * feedbackSurveyResponseStorage): SSR check, try/catch on every storage
 * touch, JSON parse/stringify, and the uid-compared-at-read rule (a
 * payload left by another account on a shared browser reads as absent,
 * so writes wholesale-replace it). Each store keeps its own
 * value-narrowing (`parse`) and its public API; the persisted wire shape
 * is UNCHANGED — `parse` receives the same raw payloads written before
 * this factory existed.
 */
interface UidScopedPayload { uid: string }

export interface UidScopedStorage<TStored extends UidScopedPayload> {
  /** Payload for this uid, or null (absent / malformed / other account / SSR / storage error). */
  readForUid: (uid: string) => TStored | null
  /** Persist a payload (carries its own uid). No-ops on SSR / storage errors. */
  write: (payload: TStored) => void
  /** Remove the key entirely. No-ops on SSR / storage errors. */
  clear: () => void
}

export default function createUidScopedStorage<TStored extends UidScopedPayload> (
  key: string,
  parse: (raw: string) => TStored | null,
): UidScopedStorage<TStored> {
  const read = (): TStored | null => {
    try {
      if (typeof window === 'undefined') return null
      const raw = window.localStorage.getItem(key)
      if (!raw) return null
      return parse(raw)
    } catch {
      // localStorage can throw (private mode / disabled) — treat as absent.
      return null
    }
  }
  return {
    readForUid: (uid) => {
      const stored = read()
      if (!stored || stored.uid !== uid) return null
      return stored
    },
    write: (payload) => {
      try {
        if (typeof window === 'undefined') return
        window.localStorage.setItem(key, JSON.stringify(payload))
      } catch {
        // localStorage unavailable — skip persistence (non-fatal).
      }
    },
    clear: () => {
      try {
        if (typeof window !== 'undefined') window.localStorage.removeItem(key)
      } catch {
        // ignore
      }
    },
  }
}
